import * as PropTypes from 'prop-types';
import _ = require('underscore');

import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import * as ArchiveConstants from 'archive/ArchiveConstants';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SchemaSummary, TagSchemaSummary} from 'constants/RegistryConstants';
import {BatchSchema} from 'constants/SampleConstants';
import SampleType from 'constants/SampleType';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import {StorableSchemaSummary} from 'samples/storables/StorableSchemaConstants';
import {
  CustomSchemaInterfaceSummary,
  SchemaInterfaceSummary,
  StaticSchemaInterfaceSummary,
} from 'schemas/schema-interface/SchemaInterfaceConstants';
import getSchemaInterfaceType from 'schemas/schema-interface/v2/getSchemaInterfaceType';
import {BenchlingAppSummary} from 'schemas/SchemaConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {SimpleRule} from 'validation_rules/components/simple_ui/types';
import {ValidationRule} from 'validation_rules/types';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';

export enum FieldType {
  AA_PART_LINK = 'ft_aa_part_link',
  ASSAY_REQUEST_LINK = 'ft_assay_request_link',
  ASSAY_RESULT_LINK = 'ft_assay_result_link',
  ASSAY_RUN_LINK = 'ft_assay_run_link',
  BATCH_LINK = 'ft_batch_link',
  BLOB_LINK = 'ft_blob_link',
  BOOLEAN = 'ft_boolean',
  DATE = 'ft_date',
  DATETIME = 'ft_datetime',
  ENTRY_LINK = 'ft_entry_link',
  BIOENTITY_LINK = 'ft_file_link',
  FLOAT = 'ft_float',
  INTEGER = 'ft_integer',
  JSON = 'ft_json',
  LONG_TEXT = 'ft_long_text',
  PART_LINK = 'ft_part_link',
  SAMPLE_LINK = 'ft_sample_link',
  SELECTOR = 'ft_selector',
  STORABLE_LINK = 'ft_storable_link',
  TEXT = 'ft_string',
  TRANSLATION_LINK = 'ft_translation_link',
  TRANSCRIPTION_LINK = 'ft_transcription_link',
}

// Based on FieldTypeCls.TYPE_DEF_CLS eg benchling/schemas/validation/link_types/bioentity_link_field_type.py.
export const FIELD_TYPE_TO_LEAF_TYPE: {[fieldType in FieldType]: LeafType} = {
  [FieldType.AA_PART_LINK]: LeafType.PROTEIN,
  [FieldType.ASSAY_REQUEST_LINK]: LeafType.REQUEST,
  [FieldType.ASSAY_RESULT_LINK]: LeafType.RESULT,
  [FieldType.ASSAY_RUN_LINK]: LeafType.RUN,
  [FieldType.BATCH_LINK]: LeafType.BATCH,
  [FieldType.BLOB_LINK]: LeafType.BLOB,
  [FieldType.BOOLEAN]: LeafType.BOOLEAN,
  [FieldType.DATE]: LeafType.DATE,
  [FieldType.DATETIME]: LeafType.DATETIME,
  [FieldType.ENTRY_LINK]: LeafType.ENTRY,
  [FieldType.BIOENTITY_LINK]: LeafType.BIOENTITY,
  [FieldType.FLOAT]: LeafType.DECIMAL,
  [FieldType.INTEGER]: LeafType.INTEGER,
  [FieldType.JSON]: LeafType.JSON,
  [FieldType.LONG_TEXT]: LeafType.TEXT,
  [FieldType.PART_LINK]: LeafType.SEQUENCE,
  [FieldType.SAMPLE_LINK]: LeafType.BIOENTITY,
  [FieldType.SELECTOR]: LeafType.DROPDOWN_OPTION,
  [FieldType.STORABLE_LINK]: LeafType.STORABLE,
  [FieldType.TEXT]: LeafType.TEXT,
  [FieldType.TRANSLATION_LINK]: LeafType.PROTEIN,
  [FieldType.TRANSCRIPTION_LINK]: LeafType.SEQUENCE,
};

export enum ComputedFieldType {
  SNAPSHOT = 'SNAPSHOT',
}

export type FieldDefinition = FieldType | ComputedFieldType;

export interface FullFieldType {
  type: FieldType;
  label: string;
}

export const TEXT_TYPE = {
  type: FieldType.TEXT,
  label: 'Text',
};

export const ANY_ENTITY = {
  id: '',
  name: 'Any entity',
};

export const DROPDOWN_FIELD_ICON = 'circle-caret-down';

// Constants for schema tag field types. The id of each constant should correspond
// to a field type as it is stored in benchling/schemas/models/schema_field.field_type.
// The label of each constant is a human readable text that is going to be displayed
// to the user.
export const FIELD_TYPES: {[fieldTypeKey: string]: FullFieldType} = {
  AA_PART_LINK: {
    type: FieldType.AA_PART_LINK,
    label: 'Part link',
  },
  ASSAY_REQUEST_LINK: {
    type: FieldType.ASSAY_REQUEST_LINK,
    label: 'Request link',
  },
  ASSAY_RESULT_LINK: {
    type: FieldType.ASSAY_RESULT_LINK,
    label: `${MODEL_NAMES.Result} link`,
  },
  ASSAY_RUN_LINK: {
    type: FieldType.ASSAY_RUN_LINK,
    label: `${MODEL_NAMES.Run} link`,
  },
  BATCH_LINK: {
    type: FieldType.BATCH_LINK,
    label: 'Batch link',
  },
  BIOENTITY_LINK: {
    type: FieldType.BIOENTITY_LINK,
    label: 'Entity link',
  },
  BLOB_LINK: {
    type: FieldType.BLOB_LINK,
    label: 'Blob link',
  },
  BOOLEAN: {
    type: FieldType.BOOLEAN,
    label: 'Boolean',
  },
  DATE: {
    type: FieldType.DATE,
    label: 'Date',
  },
  DATETIME: {
    type: FieldType.DATETIME,
    label: 'Datetime',
  },
  ENTRY_LINK: {
    type: FieldType.ENTRY_LINK,
    label: 'Entry link',
  },
  FLOAT: {
    type: FieldType.FLOAT,
    label: 'Floating point',
  },
  INTEGER: {
    type: FieldType.INTEGER,
    label: 'Integer',
  },
  JSON: {
    type: FieldType.JSON,
    label: 'JSON',
  },
  LONG_TEXT: {
    type: FieldType.LONG_TEXT,
    label: 'Long text',
  },
  PART_LINK: {
    type: FieldType.PART_LINK,
    label: 'Part link',
  },
  SAMPLE_LINK: {
    type: FieldType.SAMPLE_LINK,
    label: 'Sample link',
  },
  SELECTOR: {
    type: FieldType.SELECTOR,
    label: 'Dropdown',
  },
  STORABLE_LINK: {
    type: FieldType.STORABLE_LINK,
    label: 'Inventory link',
  },
  TEXT: TEXT_TYPE,
  TRANSLATION_LINK: {
    type: FieldType.TRANSLATION_LINK,
    label: 'Translation link',
  },
  TRANSCRIPTION_LINK: {
    type: FieldType.TRANSCRIPTION_LINK,
    label: 'Transcription link',
  },
};

const NUMERIC_FIELD_TYPES = [FIELD_TYPES.FLOAT, FIELD_TYPES.INTEGER];

export function isNumericField(schemaField: SchemaField): boolean {
  return NUMERIC_FIELD_TYPES.map((ft) => ft.type).includes(schemaField.fieldType);
}

export const FIELD_TYPES_BY_TYPE_STRING: {[fieldType: string]: FullFieldType} = _.object(
  // eslint-disable-next-line no-restricted-syntax
  _.values(FIELD_TYPES).map((fieldType) => [fieldType.type, fieldType])
);

export const BASE_FIELD_TYPES = ['TEXT', 'LONG_TEXT', 'INTEGER', 'FLOAT', 'DATE', 'DATETIME'];

export const LINK_FIELD_TYPES = [
  FIELD_TYPES.AA_PART_LINK.type,
  FIELD_TYPES.ASSAY_RESULT_LINK.type,
  FIELD_TYPES.ASSAY_RUN_LINK.type,
  FIELD_TYPES.BATCH_LINK.type,
  FIELD_TYPES.BIOENTITY_LINK.type,
  FIELD_TYPES.BLOB_LINK.type,
  FIELD_TYPES.ENTRY_LINK.type,
  FIELD_TYPES.PART_LINK.type,
  FIELD_TYPES.SAMPLE_LINK.type,
  FIELD_TYPES.SELECTOR.type,
  FIELD_TYPES.STORABLE_LINK.type,
  FIELD_TYPES.TRANSLATION_LINK.type,
  FIELD_TYPES.TRANSCRIPTION_LINK.type,
];

export const ALL_BASE_FIELD_TYPES = [...BASE_FIELD_TYPES, 'JSON'];

// Mapping between LINK_FIELD_TYPES and the label of the objects they are linking to.
export const LINKED_FIELD_ITEM_NAMES: {[fieldTypeKey: string]: string} = {
  [FieldType.AA_PART_LINK]: 'AA Part',
  [FieldType.ASSAY_REQUEST_LINK]: 'Request',
  [FieldType.ASSAY_RESULT_LINK]: 'Result',
  [FieldType.ASSAY_RUN_LINK]: 'Run',
  [FieldType.BATCH_LINK]: 'Batch',
  [FieldType.BIOENTITY_LINK]: 'Entity',
  [FieldType.BLOB_LINK]: 'Blob',
  [FieldType.ENTRY_LINK]: 'Entry',
  [FieldType.PART_LINK]: 'Part',
  [FieldType.TRANSLATION_LINK]: 'Translation',
  [FieldType.TRANSCRIPTION_LINK]: 'Transcription',
  // SAMPLE_LINK, SELECTOR, STORABLE_LINK are intentionally omitted
  // because the user facing label may depend on the actual values of the linked objects
};

// Keep in sync with the unit_id_only_if_float_or_int CheckConstraint in benchling/schemas/models/schema_field.py
export const UNIT_AWARE_FIELD_TYPES = [FieldType.FLOAT, FieldType.INTEGER];
export const UNIT_AWARE_FIELD_DEFINITIONS: Array<FieldDefinition> = UNIT_AWARE_FIELD_TYPES;

export type SupportedRequiredLinkFolderItemType =
  | ModelTypeConstants.BASIC_FOLDER_ITEM
  | ModelTypeConstants.PROTEIN
  | ModelTypeConstants.SEQUENCE
  | ModelTypeConstants.MIXTURE
  | ModelTypeConstants.MOLECULE;

// Keep in sync with server-side benchling/schemas/models/schema_field.py
// `SUPPORTED_REQUIRED_LINK_FOLDER_ITEM_TYPES`.
export const SUPPORTED_REQUIRED_LINK_FOLDER_ITEM_TYPES: Array<SupportedRequiredLinkFolderItemType> = [
  ModelTypeConstants.BASIC_FOLDER_ITEM,
  ModelTypeConstants.PROTEIN,
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.MIXTURE,
  ModelTypeConstants.MOLECULE,
];

export interface FullRequiredLinkFolderItemType {
  type: SupportedRequiredLinkFolderItemType;
}

export const REQUIRED_LINK_FOLDER_ITEM_TYPES: {[folderItemTypeKey: string]: FullRequiredLinkFolderItemType} =
  {
    BASIC_FOLDER_ITEM: {
      type: ModelTypeConstants.BASIC_FOLDER_ITEM,
    },
    PROTEIN: {
      type: ModelTypeConstants.PROTEIN,
    },
    SEQUENCE: {
      type: ModelTypeConstants.SEQUENCE,
    },
    MIXTURE: {
      type: ModelTypeConstants.MIXTURE,
    },
    MOLECULE: {
      type: ModelTypeConstants.MOLECULE,
    },
  };

export const REQUIRED_LINK_FOLDER_ITEM_TYPES_BY_TYPE_STRING: {
  [folderItemType: string]: FullRequiredLinkFolderItemType;
} = _.object(
  // eslint-disable-next-line no-restricted-syntax
  _.values(REQUIRED_LINK_FOLDER_ITEM_TYPES).map((fieldType) => [fieldType.type, fieldType])
);

export const TAG_SCHEMA_EDGE = PropTypes.shape({
  name: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  apiId: PropTypes.string.isRequired,
  systemName: PropTypes.string.isRequired,
  from: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    typecast: PropTypes.shape({
      apiId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      folderItemType: PropTypes.string,
    }),
  }),
  to: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    typecast: PropTypes.shape({
      apiId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      folderItemType: PropTypes.string,
    }),
  }),
});

export const TAG_SCHEMA_PATH = PropTypes.arrayOf(TAG_SCHEMA_EDGE);

export const TAG_SCHEMA_GRAPH = PropTypes.shape({
  edges: TAG_SCHEMA_PATH,
});

export interface SchemaFieldVertex {
  name: string;
  id: string;
  folderItemType?: string;
  typecast?: {
    apiId: string;
    name: string;
    folderItemType?: string;
  };
}

export interface SchemaFieldEdge {
  name: string;
  id: number;
  apiId: string;
  systemName: string;
  from: SchemaFieldVertex | null;
  to: SchemaFieldVertex | null;
}

export interface SchemaFieldPath {
  id: number;
  edges: Array<SchemaFieldEdge>;
}

export interface TargetSchemaToPaths {
  [targetTagSchemaId: string]: Array<Array<SchemaFieldEdge>>;
}

export type RequiredLink =
  | RequiredLinkTagSchema
  | RequiredLinkSchemaInterface
  | RequiredLinkFolderItemType
  | RequiredLinkBatchSchema
  | RequiredLinkStorableSchema
  | RequiredLinkSample
  | RequiredLinkAssaySchema
  | RequiredLinkAnyEntity;

export type RequiredLinkKey = keyof (RequiredLinkTagSchema &
  RequiredLinkBioentitySample &
  RequiredLinkFolderItemType &
  RequiredLinkBatchSchema &
  RequiredLinkStorableSchema &
  RequiredLinkSample &
  RequiredLinkAssaySchema &
  RequiredLinkAnyEntity);

export interface RequiredLinkTagSchema {
  tagSchema: TagSchemaSummary | TagSchemaSummaryGraphQL | null;
  entitySchemaFieldPath: SchemaFieldPath | null;
  folderItemType: null;
  schemaInterface: null;
}
export interface RequiredLinkSchemaInterface {
  tagSchema: null;
  schemaInterface: SchemaInterfaceSummary;
  folderItemType: null;
  entitySchemaFieldPath: null;
}

export interface RequiredLinkCustomSchemaInterface {
  tagSchema: null;
  schemaInterface: CustomSchemaInterfaceSummary;
  folderItemType: null;
  entitySchemaFieldPath: null;
}

export interface RequiredLinkStaticSchemaInterface {
  tagSchema: null;
  schemaInterface: StaticSchemaInterfaceSummary;
  folderItemType: null;
  entitySchemaFieldPath: null;
}

export interface RequiredLinkAnyEntity {
  tagSchema: null;
  schemaInterface: null;
  folderItemType: null;
  entitySchemaFieldPath: null;
}

export function isRequiredLinkCustomSchemaInterface(
  data?: RequiredLink | null
): data is RequiredLinkCustomSchemaInterface {
  return (
    isRequiredLinkSchemaInterface(data) &&
    getSchemaInterfaceType(data.schemaInterface.id) === DomainGraphType.CUSTOM_SCHEMA_INTERFACE
  );
}

export function isRequiredLinkStaticSchemaInterface(
  data?: RequiredLink | null
): data is RequiredLinkStaticSchemaInterface {
  return (
    isRequiredLinkSchemaInterface(data) &&
    getSchemaInterfaceType(data.schemaInterface.id) === DomainGraphType.STATIC_SCHEMA_INTERFACE
  );
}

/**
 * Check if the required link is for a either a custom or static schema interface.
 */
export function isRequiredLinkSchemaInterface(
  data?: RequiredLink | null
): data is RequiredLinkSchemaInterface {
  return (
    data != null &&
    data.hasOwnProperty('schemaInterface') &&
    (data as RequiredLinkSchemaInterface).schemaInterface != null
  );
}

export function isRequiredLinkTagSchema(
  data?: RequiredLink | null
): data is RequiredLinkTagSchema & {tagSchema: TagSchemaSummary} {
  return (
    data != null &&
    data.hasOwnProperty('tagSchema') &&
    (data as RequiredLinkTagSchema | RequiredLinkFolderItemType).tagSchema != null
  );
}

export function isRequiredLinkBatchSchema(
  data?: RequiredLink | null
): data is RequiredLinkBatchSchema & {batchSchema: BatchSchema} {
  return (
    data != null &&
    data.hasOwnProperty('batchSchema') &&
    (data as RequiredLinkBatchSchema).batchSchema != null
  );
}

export function isRequiredLinkAnyEntity(data?: RequiredLink | null): data is RequiredLinkAnyEntity {
  const properties: Array<keyof RequiredLinkAnyEntity> = [
    'tagSchema',
    'schemaInterface',
    'folderItemType',
    'entitySchemaFieldPath',
  ];
  return (
    data != null &&
    properties.every(
      (property) => data.hasOwnProperty(property) && (data as RequiredLinkAnyEntity)[property] == null
    )
  );
}

export interface RequiredLinkFolderItemType {
  tagSchema: null;
  entitySchemaFieldPath: null;
  folderItemType: string | null;
  schemaInterface: null;
}

export function isRequiredLinkFolderItemType(
  data?: RequiredLink | null
): data is RequiredLinkFolderItemType & {folderItemType: string} {
  return (
    data != null &&
    data.hasOwnProperty('folderItemType') &&
    (data as RequiredLinkTagSchema | RequiredLinkFolderItemType).folderItemType != null
  );
}

export interface RequiredLinkBatchSchema {
  batchSchema: BatchSchema | null;
  batchSchemaGraph: SchemaFieldPath | null;
}

export interface RequiredLinkStorableSchema {
  storableSchema: StorableSchemaSummary | null;
}

export function isRequiredLinkStorableSchemaOrAnyInventory(
  data?: RequiredLink | null
): data is RequiredLinkStorableSchema {
  return data != null && data.hasOwnProperty('storableSchema');
}

export function isRequiredLinkStorableSchema(
  data?: RequiredLink | null
): data is RequiredLinkStorableSchema & {storableSchema: StorableSchemaSummary} {
  return isRequiredLinkStorableSchemaOrAnyInventory(data) && data.storableSchema != null;
}

type RequiredLinkSample = RequiredLinkBatchSample | RequiredLinkBioentitySample;

export function isRequiredLinkSample(requiredLink: RequiredLink): requiredLink is RequiredLinkSample {
  return requiredLink.hasOwnProperty('sampleType');
}

interface RequiredLinkBatchSample {
  sampleType: SampleType.BATCH;
  batchSchema: BatchSchema | null;
}

interface RequiredLinkBioentitySample {
  sampleType: SampleType.BIOENTITY;
  tagSchema: TagSchemaSummary | null;
}

export interface RequiredLinkAssaySchema {
  assaySchema: AssaySchema | null;
}

export function isRequiredLinkAssaySchema(
  data?: RequiredLink | null
): data is RequiredLinkAssaySchema & {assaySchema: AssaySchema} {
  return (
    data != null &&
    data.hasOwnProperty('assaySchema') &&
    (data as RequiredLinkAssaySchema).assaySchema != null
  );
}

export const REQUIRED_LINK = PropTypes.shape({
  batchSchema: PropTypes.object,
  batchSchemaGraph: TAG_SCHEMA_GRAPH,
  tagSchema: PropTypes.shape({
    id: PropTypes.string.isRequired,
    folderItemType: PropTypes.oneOf(SUPPORTED_REQUIRED_LINK_FOLDER_ITEM_TYPES),
  }),
  folderItemType: PropTypes.oneOf(SUPPORTED_REQUIRED_LINK_FOLDER_ITEM_TYPES),
  sampleType: PropTypes.oneOf(Object.values(SampleType)),
});

type RequiredLinkWithTagSchema =
  | RequiredLinkTagSchema
  | RequiredLinkBioentitySample
  | RequiredLinkFolderItemType;

function requiredLinkHasTagSchema(requiredLink: RequiredLink): requiredLink is RequiredLinkWithTagSchema {
  return (requiredLink as RequiredLinkWithTagSchema).tagSchema != null;
}

export function getRequiredLinkTagSchema(field: {
  requiredLink: RequiredLink | null;
}): TagSchemaSummary | TagSchemaSummaryGraphQL | null {
  return requiredLinkToTagSchema(field.requiredLink);
}

function requiredLinkToTagSchema(
  requiredLink: RequiredLink | null
): TagSchemaSummary | TagSchemaSummaryGraphQL | null {
  if (requiredLink && requiredLinkHasTagSchema(requiredLink)) {
    return requiredLink.tagSchema;
  }
  return null;
}

export function getRequiredLinkStaticSchemaInterface(
  field: SchemaField
): StaticSchemaInterfaceSummary | null {
  return requiredLinkToStaticSchemaInterface(field.requiredLink);
}

function requiredLinkToStaticSchemaInterface(
  requiredLink: RequiredLink | null
): StaticSchemaInterfaceSummary | null {
  if (requiredLink && isRequiredLinkStaticSchemaInterface(requiredLink)) {
    return requiredLink.schemaInterface;
  }
  return null;
}

export function getRequiredLinkCustomSchemaInterface(field: SchemaField): SchemaInterfaceSummary | null {
  return requiredLinkToCustomSchemaInterface(field.requiredLink);
}

function requiredLinkToCustomSchemaInterface(
  requiredLink: RequiredLink | null
): SchemaInterfaceSummary | null {
  if (requiredLink && isRequiredLinkCustomSchemaInterface(requiredLink)) {
    return requiredLink.schemaInterface;
  }
  return null;
}

export function getRequiredLinkTagSchemaOrCustomSchemaInterface(field: SchemaField): SchemaSummary | null {
  return getRequiredLinkTagSchema(field) || getRequiredLinkCustomSchemaInterface(field);
}

function requiredLinkHasFolderItemType(
  requiredLink: RequiredLink
): requiredLink is RequiredLinkFolderItemType {
  return (requiredLink as RequiredLinkFolderItemType).folderItemType !== undefined;
}

export function getRequiredLinkFolderItemType(field: SchemaField): string | null {
  if (field.requiredLink && requiredLinkHasFolderItemType(field.requiredLink)) {
    return field.requiredLink.folderItemType;
  }
  return null;
}

export function getFolderItemType(field: SchemaField): string | null {
  const maybeGenericFolderItemType = getRequiredLinkFolderItemType(field);
  if (maybeGenericFolderItemType != null) {
    return maybeGenericFolderItemType;
  }

  const maybeSpecificTagSchema = getRequiredLinkTagSchema(field);
  if (maybeSpecificTagSchema != null) {
    return maybeSpecificTagSchema.folderItemType;
  }

  return null;
}

type RequiredLinkWithBatchSchema = RequiredLinkBatchSchema | RequiredLinkBatchSample;

function requiredLinkHasBatchSchema(requiredLink: RequiredLink): requiredLink is RequiredLinkWithBatchSchema {
  return (requiredLink as RequiredLinkWithBatchSchema).batchSchema !== undefined;
}

export function getRequiredLinkBatchSchema(field: SchemaField): BatchSchema | null {
  return requiredLinkToBatchSchema(field.requiredLink);
}

function requiredLinkToBatchSchema(requiredLink: RequiredLink | null): BatchSchema | null {
  if (requiredLink && requiredLinkHasBatchSchema(requiredLink)) {
    return requiredLink.batchSchema;
  }
  return null;
}

export function getRequiredLinkStorableSchema(field: SchemaField): StorableSchemaSummary | null {
  return requiredLinkToStorableSchema(field?.requiredLink);
}

function requiredLinkToStorableSchema(requiredLink: RequiredLink | null): StorableSchemaSummary | null {
  if (requiredLink) {
    const storableSchema = (requiredLink as RequiredLinkStorableSchema).storableSchema;
    if (storableSchema !== undefined) {
      return storableSchema;
    }
  }
  return null;
}

export function getRequiredLinkAssaySchema(field: SchemaField): AssaySchema | null {
  return requiredLinkToAssaySchema(field.requiredLink);
}

function requiredLinkToAssaySchema(requiredLink: RequiredLink | null): AssaySchema | null {
  if (requiredLink) {
    const assaySchema = (requiredLink as RequiredLinkAssaySchema).assaySchema;
    if (assaySchema !== undefined) {
      return assaySchema;
    }
  }
  return null;
}

export function getRequiredLinkSchemaId(field: SchemaField): string | null {
  return requiredLinkToSchemaId(field.requiredLink);
}

export function requiredLinkToSchemaId(requiredLink: RequiredLink | null): string | null {
  const schemaGetters = [
    requiredLinkToBatchSchema,
    requiredLinkToTagSchema,
    requiredLinkToStorableSchema,
    requiredLinkToAssaySchema,
    requiredLinkToCustomSchemaInterface,
    requiredLinkToStaticSchemaInterface,
  ];
  for (const schemaGetterFn of schemaGetters) {
    const schema = schemaGetterFn(requiredLink);
    if (schema) {
      return schema.id;
    }
  }

  return null;
}

// TODO(TypeScript): Implement this as a tagged union of all schema field types so that we never have to cast
// (e.g. what we're currently doing in TypedValueResolution::resolveTypedValueForSchemaFieldWithoutLegalText)
export interface SchemaField {
  id: number;
  dbId: number;
  apiId: string;
  name: string;
  systemName: string;
  displayName: string;
  description?: string | null;
  tooltipText?: string | null;
  fieldType: FieldType;
  isRequired: boolean;
  isMulti: boolean;
  isComputed: boolean;
  isConvertedFromLink: boolean;
  isSnapshot: boolean;
  isIntegration: boolean;
  isParentLink: boolean;
  requiredLink: RequiredLink | null;
  schemaFieldSelectorId?: string | null;
  legalTextSelectorId?: string | null;
  archiveRecord: ArchiveConstants.ArchiveRecord | null;
  numericMin?: number | null;
  numericMax?: number | null;
  decimalPrecision?: number | null;
  lookupConfig?: LookupConfigSpec;
  isUneditable?: boolean; // TODO(BNCH-56776): Make required
  relatedBenchlingApps?: Array<BenchlingAppSummary>;
  appliedFieldset?: SchemaInterfaceSummary | null;
  unitApiIdentifier?: string | null;
  unitSymbol?: string | null;
  simpleRules?: Array<SimpleRule>;
  advancedRules?: Array<ValidationRule>;
}

// TODO(BNCH-30720) Doesn't exactly match typescript type
export const SCHEMA_FIELD_SHAPE = PropTypes.shape({
  id: PropTypes.number,
  apiId: PropTypes.string,
  name: PropTypes.string.isRequired,
  // Names of assay schema fields must be valid SQL identifiers, so we allow them to have
  // another, more human-readable name. For fields on other kinds of schemas, this is always
  // equal to `name`.
  displayName: PropTypes.string.isRequired,
  systemName: PropTypes.string,
  fieldType: PropTypes.string,
  isParentLink: PropTypes.bool, // Derived from presence of GQL SchemaFieldNode.childEntityLinkConfig
  isRequired: PropTypes.bool.isRequired,
  isComputed: PropTypes.bool.isRequired,
  isConvertedFromLink: PropTypes.bool.isRequired,
  isSnapshot: PropTypes.bool,
  isIntegration: PropTypes.bool.isRequired,
  isMulti: PropTypes.bool, // TODO: make this required to match the TS type
  requiredLink: REQUIRED_LINK,
  schemaFieldSelectorId: PropTypes.string,
  // See legal_text_selectors.py for more information
  legalTextSelectorId: PropTypes.string,
  archiveRecord: ArchiveConstants.ARCHIVE_RECORD_SHAPE,
});

export const SCHEMA_FIELD_DATA_TYPE_SHAPE = PropTypes.shape({
  fieldType: PropTypes.string,
  isRequired: PropTypes.bool.isRequired,
  isMulti: PropTypes.bool,
  requiredLink: REQUIRED_LINK,
});

export const SCHEMA_FIELD_RENDER_DATA_SHAPE = PropTypes.shape({
  name: PropTypes.string.isRequired,
  fieldType: PropTypes.string.isRequired,
});

export interface InterfaceField {
  id: number;
  apiId: string;
  systemName: string;
  displayName: string;
  fieldType: FieldType;
  isRequired: boolean;
  isMulti: boolean;
  isComputed: boolean;
  requiredLink: RequiredLink | null;
  schemaFieldSelectorId: string | null;
  unitApiIdentifier?: string | null;
  unitSymbol?: string | null;
}

/**
 * Data required to render information about a schema field. For example may be used when
 * rendering a schema tag value.
 */
export interface SchemaFieldRenderData {
  name: string;
  fieldType: FieldType;
  unitSymbol?: string | null;
}

export type LinkSchemaField =
  | BioentityLinkSchemaField
  | EntryLinkSchemaField
  | BatchLinkSchemaField
  | StorableLinkSchemaField
  | SampleLinkSchemaField
  | AssayLinkSchemaField;

export function isBioentityLinkFieldWithTargetSchema(
  field: SchemaField
): field is BioentityLinkFieldWithTargetSchema {
  return (
    isBioentityLinkSchemaField(field) &&
    (requiredLinkHasTagSchema(field.requiredLink) || isRequiredLinkCustomSchemaInterface(field.requiredLink))
  );
}

export interface BioentityLinkFieldWithTagSchema extends SchemaField {
  fieldType:
    | FieldType.AA_PART_LINK
    | FieldType.BIOENTITY_LINK
    | FieldType.PART_LINK
    | FieldType.TRANSLATION_LINK
    | FieldType.TRANSCRIPTION_LINK;
  requiredLink: RequiredLinkTagSchema;
}

interface BioentityLinkFieldWithRequiredType extends SchemaField {
  fieldType:
    | FieldType.AA_PART_LINK
    | FieldType.BIOENTITY_LINK
    | FieldType.PART_LINK
    | FieldType.TRANSLATION_LINK
    | FieldType.TRANSCRIPTION_LINK;
  requiredLink: RequiredLinkFolderItemType;
}

export interface BioentityLinkFieldWithSchemaInterface extends SchemaField {
  fieldType:
    | FieldType.AA_PART_LINK
    | FieldType.BIOENTITY_LINK
    | FieldType.PART_LINK
    | FieldType.TRANSLATION_LINK
    | FieldType.TRANSCRIPTION_LINK;
  requiredLink: RequiredLinkSchemaInterface;
}

export type BioentityLinkFieldWithTargetSchema =
  | BioentityLinkFieldWithTagSchema
  | BioentityLinkFieldWithSchemaInterface;

// This bioentity link field structure is only valid for non-registry schemas
interface BioentityLinkFieldWithoutTarget extends SchemaField {
  fieldType: FieldType.BIOENTITY_LINK;
  requiredLink: {
    tagSchema: null;
    folderItemType: null;
    entitySchemaFieldPath: null;
    schemaInterface: null;
  };
}

export type BioentityLinkSchemaField =
  | BioentityLinkFieldWithTargetSchema
  | BioentityLinkFieldWithRequiredType
  | BioentityLinkFieldWithoutTarget
  | BioentityLinkFieldWithSchemaInterface;

export interface EntryLinkSchemaField extends SchemaField {
  fieldType: FieldType.ENTRY_LINK;
  requiredLink: RequiredLinkTagSchema;
}

export interface BatchLinkSchemaField extends SchemaField {
  fieldType: FieldType.BATCH_LINK;
  requiredLink: RequiredLinkBatchSchema;
}

export interface StorableLinkSchemaField extends SchemaField {
  fieldType: FieldType.STORABLE_LINK;
  requiredLink: RequiredLinkStorableSchema;
}

export type SampleLinkSchemaField = BatchSampleLinkSchemaField | BioentitySampleLinkSchemaField;

export interface BatchSampleLinkSchemaField extends SchemaField {
  fieldType: FieldType.SAMPLE_LINK;
  requiredLink: RequiredLinkBatchSample;
}

export interface BioentitySampleLinkSchemaField extends SchemaField {
  fieldType: FieldType.SAMPLE_LINK;
  requiredLink: RequiredLinkBioentitySample;
}

export function isSampleFieldForBioentity(
  field: SampleLinkSchemaField
): field is BioentitySampleLinkSchemaField {
  return field.requiredLink.sampleType === SampleType.BIOENTITY;
}

export function isBioentityLinkSchemaField(field: {fieldType: FieldType}): field is BioentityLinkSchemaField {
  return [
    FieldType.AA_PART_LINK,
    FieldType.BIOENTITY_LINK,
    FieldType.PART_LINK,
    FieldType.TRANSLATION_LINK,
    FieldType.TRANSCRIPTION_LINK,
  ].includes(field.fieldType);
}

export interface AssayLinkSchemaField extends SchemaField {
  fieldType: FieldType.ASSAY_RESULT_LINK | FieldType.ASSAY_RUN_LINK | FieldType.ASSAY_REQUEST_LINK;
  requiredLink: RequiredLinkAssaySchema;
}

interface BaseFieldOption {
  label: string | JSX.Element;
  pureTextLabel?: string;
  icon?: BenchlingIconName;
  extraClasses?: string;
  // text rendered underneath main label
  metadata?: string | null;
}

export interface LeafFieldOption extends BaseFieldOption {
  fieldDefinition: FieldDefinition;
  requiredLink: RequiredLink | null;
  schemaFieldSelectorId?: string | null;
  disabled?: boolean;
  disabledMessage?: string;
}

export interface FieldOptionHeader extends BaseFieldOption {
  isHeader: true;
}

// This interface is recursive, but its associated PropTypes are not
export interface FieldOptionSubmenu extends BaseFieldOption {
  submenu: Array<FieldOption>;
}

export type FieldOption = LeafFieldOption | FieldOptionSubmenu | FieldOptionHeader;

export function isFieldOptionSubmenu(fieldOption: FieldOption): fieldOption is FieldOptionSubmenu {
  return fieldOption.hasOwnProperty('submenu');
}

export function isFieldOptionHeader(fieldOption: FieldOption): fieldOption is FieldOptionHeader {
  return fieldOption.hasOwnProperty('isHeader');
}

export function isFieldOptionLeafField(fieldOption: FieldOption): fieldOption is LeafFieldOption {
  return fieldOption.hasOwnProperty('requiredLink');
}

export function getFieldOptionPureTextLabel(fieldOption: FieldOption): string {
  if (fieldOption.pureTextLabel) {
    return fieldOption.pureTextLabel;
  }
  assert(
    typeof fieldOption.label === 'string',
    'A FieldOption must either have a `label` of type string or define `pureTextLabel`'
  );
  return fieldOption.label;
}

export const FIELD_OPTION = PropTypes.shape({
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  fieldDefinition: PropTypes.string.isRequired,
  schemaFieldSelectorId: PropTypes.string,
  requiredLink: REQUIRED_LINK,
});

export const FIELD_OPTION_SUBMENU = PropTypes.shape({
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  submenu: PropTypes.arrayOf(FIELD_OPTION).isRequired,
  pureTextLabel: PropTypes.string,
});

export const FIELD_OPTION_HEADER = PropTypes.shape({
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  isHeader: PropTypes.bool,
});

// Note that LOOKUP (defined on the BE) is intentionally excluded here for the time being, because it is not
// yet supported for live computed fields
export enum ComputedFieldPropertyType {
  FORMULA = 'FORMULA',
  // General dependency types
  FIELD = 'FIELD',
  LINKED_RESULT_FIELDS = 'LINKED_RESULT_FIELDS',
  LITERAL = 'LITERAL',
  API_IDENTIFIER = 'API_IDENTIFIER',
  REGISTRATION_STATUS = 'REGISTRATION_STATUS',

  // Biological properties
  AMINO_ACIDS = 'AMINO_ACIDS',
  BASES = 'BASES',
  BASES_WITH_MODS = 'BASES_WITH_MODS',
  BASES_WITH_MODS_AND_MONOMER_MW = 'BASES_WITH_MODS_AND_MONOMER_MW',
  PRIMER_OLIGOS = 'PRIMER_OLIGOS',
  PARENT_SEQUENCE = 'PARENT_SEQUENCE',
  DNA_ALIGNMENTS = 'DNA_ALIGNMENTS',

  CHEMICAL_STRUCTURE = 'CHEMICAL_STRUCTURE',
  COMPLEX_POLYMER_WITH_ENTITY_ASSOCIATIONS = 'COMPLEX_POLYMER_WITH_ENTITY_ASSOCIATIONS',
  ANTIBODY_STRUCTURE = 'ANTIBODY_STRUCTURE',
}

export interface ComputedSchemaFieldDependencyRequestFieldPathEdge {
  apiId: string;
  typecastSchemaApiId?: string | null;
}

export interface ComputedSchemaFieldDependencyRequestFieldPath {
  edges: Array<ComputedSchemaFieldDependencyRequestFieldPathEdge>;
}

export interface ComputedSchemaFieldDependencyRequest {
  assaySchemaName?: string;
  fieldPath: ComputedSchemaFieldDependencyRequestFieldPath | null;
  literalValue?: string;
  property: ComputedFieldPropertyType;
  propertyFieldName?: string;
  targetLinkFieldName?: string;
}

export interface ComputedSchemaFieldDependencyResponse {
  name: string;
  assaySchemaName: string | null;
  fieldPath: SchemaFieldPath | null;
  literalValue: string | null;
  property: ComputedFieldPropertyType;
  propertyFieldApiId?: string;
  propertyFieldName?: string;
  targetLinkFieldName?: string;
}

export interface ComputedSchemaField extends SchemaField {
  isComputed: true;
  dependencies: Array<ComputedSchemaFieldDependencyResponse>;
  formulaName: string;
}

export interface FormulaArg {
  name: string;
  isRequired: boolean;
}

export interface FormulaSpec {
  args: Array<FormulaArg>;
  alias: string;
}

export function isSnapshotField(schemaField: SchemaField): schemaField is SnapshotField {
  return schemaField.isSnapshot;
}

export interface SnapshotField extends SchemaField {
  isSnapshot: true;
  isRequired: false;
  lookupConfig: LookupConfigSpec;
}
